import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import moment from "moment";
import { collection, getDocs, getDoc, updateDoc, doc, arrayUnion, serverTimestamp } from "firebase/firestore";
import { db, app } from "../firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import "../pages/CalendarStyles.css";
import "../pages/JobDetailsShared.css";
import {
  applyOccurrenceOverrideToRenderedJob,
  getOccurrenceOverrideRenderJobs,
  mergeRenderedRecurringJobs,
  saveRecurringJobEdit,
  getRecurrenceEndKey,
  getExcludedDateSet,
  closeOrDeleteJob,
} from "../utils/recurringEditUtils";
import {
  refreshKeepingView,
  restoreScrollAfterRefresh,
  getCompletionOccurrenceKey,
  occurrenceIsInDateList,
} from "../utils/recurringOccurrenceUtils";
import QuickJobFieldEditor from "../components/QuickJobFieldEditor";
import CollapsibleActionSection from "../components/CollapsibleActionSection";
import JobHistoryPopup from "../components/JobHistoryPopup";
import JobCompletionInvoiceModal from "../components/JobCompletionInvoiceModal";
import { JobCheckInButton, JobVisitHistory } from "../clock/JobCheckIn";
import AvailabilityView from "./AvailabilityView";
import Scheduling from "../pages/Scheduling";
import { buildTimeOffByDate, shouldShowOnCalendar } from "../utils/timeOffUtils";
import {
  TimeOffChip,
  TimeOffLegendItems,
  OutTodayList,
  TimeOffDetailsModal,
} from "../components/TimeOffCalendarPieces";
import {
  getMaterialsGroupId,
  fetchAllJobMaterials,
  loadMaterialsForJob,
  saveMaterialsForJob,
} from "../utils/materialsUtils";


const LoadingSpinner = ({ label = "Saving" }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
    <style>{`@keyframes jmc-note-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,0.65)",
        borderTopColor: "#111827",
        borderRadius: "50%",
        display: "inline-block",
        animation: "jmc-note-spin 0.8s linear infinite",
      }}
    />
    {label}
  </span>
);

const EmbeddedCalendar = ({
  jobs,
  userRole,
  onBackToJobs,
  onJobUpdated,
  onEditJob,
  onAddSupervisorNote,
  onContinueJob,
  onAddTotal,
  onAddReceipt,
  onDeleteJob,
  onChecklist,
  // Tech view: only show occurrences assigned to this person (techAssign).
  onlyTechName = "",
  // Job check-in (from Home's useJobVisits) and the "can complete" gate.
  jobVisits = null,
  canCompleteJob = null,
}) => {
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  // Persisted so that a completion-triggered reload comes back to the SAME
  // calendar view and date. Home already persists its activeTab, so together
  // these mean "refresh while in Calendar → still in Calendar, same week".
  const EMBEDDED_CALENDAR_VIEW_KEY = "priority_embedded_calendar_view";
  const EMBEDDED_CALENDAR_DATE_KEY = "priority_embedded_calendar_date";

  const [selectedDate, setSelectedDate] = useState(() => {
    try {
      const stored = sessionStorage.getItem(EMBEDDED_CALENDAR_DATE_KEY);
      const parsed = stored ? moment(stored, "YYYY-MM-DD", true) : null;
      if (parsed && parsed.isValid()) return parsed.toDate();
    } catch (e) {
      // ignore
    }
    return new Date();
  });

  const [view, setView] = useState(() => {
    try {
      const stored = sessionStorage.getItem(EMBEDDED_CALENDAR_VIEW_KEY);
      if (["day", "week", "month", "availability"].includes(stored)) return stored;
    } catch (e) {
      // ignore
    }
    return "month";
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(EMBEDDED_CALENDAR_VIEW_KEY, view);
      sessionStorage.setItem(
        EMBEDDED_CALENDAR_DATE_KEY,
        moment(selectedDate).format("YYYY-MM-DD")
      );
    } catch (e) {
      // ignore
    }
  }, [view, selectedDate]);

  useEffect(() => {
    restoreScrollAfterRefresh();
  }, []);

  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedTimeOffRequest, setSelectedTimeOffRequest] = useState(null);
  // Availability tab: the open block being scheduled, and jobs scheduled from
  // here so they show immediately even before the parent list refreshes.
  const [scheduleDraft, setScheduleDraft] = useState(null);
  const [locallyScheduledJobs, setLocallyScheduledJobs] = useState([]);
  // Complete Job & Create Invoice form.
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [completionSubmitted, setCompletionSubmitted] = useState(false);
  const isStaffRole = ["Admin", "Manager", "Assistant"].includes(userRole);

  // Lock background scrolling while the Job Details popup is open so the
  // popup can't be scrolled past — it has to be closed to interact with
  // the page behind it.
  useEffect(() => {
    if (!selectedJob) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedJob]);

  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState({});

  const [showNotePopup, setShowNotePopup] = useState(false);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);

  const [userFullName, setUserFullName] = useState("");
  const [propertiesList, setPropertiesList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [showMaterialsPopup, setShowMaterialsPopup] = useState(false);
  const [materialsDraft, setMaterialsDraft] = useState([]);
  const [jobMaterials, setJobMaterials] = useState({});
  const getMaterialsForJob = (job) =>
    jobMaterials[getMaterialsGroupId(job)] || [];

  const functions = getFunctions(app, "us-central1");
  const sendJobNoteEmailCallable = httpsCallable(functions, "sendJobNoteEmail");
  
  const timeWindowOptions = [
    "ANY",
    "8AM - 10AM",
    "9AM - 11AM",
    "10AM - 12PM",
    "11AM - 1PM",
    "12PM - 2PM",
    "1PM - 3PM",
    "2PM - 4PM",
    "3PM - 5PM",
    "4PM - 6PM",
    "Custom Time",
  ];

  /* ================= FETCH SUPPORT DATA ONLY ================= */
  useEffect(() => {
    if (showMaterialsPopup && selectedJob) {
      const groupId = getMaterialsGroupId(selectedJob);
      (async () => {
        const currentMaterials =
          jobMaterials[groupId] || (await loadMaterialsForJob(selectedJob));
        setJobMaterials((previous) => ({ ...previous, [groupId]: currentMaterials }));
        setMaterialsDraft(currentMaterials.map(normalizeMaterial));
      })();
    }
  }, [showMaterialsPopup, selectedJob]);

  useEffect(() => {
    const loadAllJobMaterials = async () => {
      try {
        setJobMaterials(await fetchAllJobMaterials());
      } catch (error) {
        console.error("Error fetching job materials:", error);
      }
    };
    loadAllJobMaterials();
  }, []);
  
  useEffect(() => {
    const fetchTimeOff = async () => {
      const snap = await getDocs(collection(db, "timeOff"));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Approved requests + logged call-outs / left early / came in late.
      setTimeOffRequests(data.filter(shouldShowOnCalendar));
    };

    const fetchProperties = async () => {
      const snap = await getDocs(collection(db, "properties"));
      setPropertiesList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };

    const fetchUsers = async () => {
      const snap = await getDocs(collection(db, "users"));
      setUsersList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };

    const fetchMe = async () => {
      const auth = getAuth();
      const me = auth.currentUser;
      if (!me) return;

      const snap = await getDocs(collection(db, "users"));
      const u = snap.docs.find((d) => d.id === me.uid);
      if (u) {
        setUserFullName(`${u.data().firstName} ${u.data().lastName}`);
      }
    };

    fetchTimeOff();
    fetchProperties();
    fetchUsers();
    fetchMe();
  }, []);

  const getDurationMismatch = (job) => {
    if (!job.startDate || !job.endDate || !job.jobPlan?.estimatedDays) return null;
  
    const scheduledDays =
      moment(job.endDate).diff(moment(job.startDate), "days") + 1;
  
    if (scheduledDays !== job.jobPlan.estimatedDays) {
      return {
        scheduledDays,
        estimatedDays: job.jobPlan.estimatedDays,
      };
    }
  
    return null;
  };

const getRecurrenceValue = (job) =>
  (job.recurrence || job.reccurance || job.recurring || job.repeat || "").toString().trim();

const isRecurringJob = (job) => {
  const type = getRecurrenceType(job);
  return type && type !== "none";
};

const getOccurrenceDateKey = (job) => {
  if (isRecurringJob(job)) {
    return toDateKey(job.occurrenceDate || job.startDate);
  }

  return toDateKey(job.startDate);
};

const getWeekStart = (date) => moment(date).day(0).startOf("day");
const getWeekEnd = (date) => moment(date).day(6).endOf("day");

const isOccurrenceCompleted = (job) =>
  occurrenceIsInDateList(job, job.completedDates);

const isOccurrenceInvoiced = (job) =>
  occurrenceIsInDateList(job, job.invoicedDates);

const isClosedJob = (job) => {
  if (isRecurringJob(job)) {
    return isOccurrenceCompleted(job) || isOccurrenceInvoiced(job);
  }

  const status = (job?.status || "").trim().toLowerCase();
  return status === "completed" || status === "invoice sent";
};

const getJobTileColor = (job) => {
  if (isClosedJob(job)) return "#D96C6C"; // soft warm red override

  const type = (job?.serviceType || "").toLowerCase().trim();

  if (type === "proposal") return "#6FAE7C";       // warm soft green
  if (type === "meeting") return "#D9B95B";        // warm muted yellow
  if (type === "inspection") return "#D9B95B";     // warm muted yellow
  if (type === "action item" || type === "action items") return "#8E6BBE"; // warm soft purple

  return job.color || "#5B8FD9";                   // soft warm blue
};

const getJobDisplayText = (job) =>
  `${isClosedJob(job) ? "✕ " : ""}${job.siteName || "Untitled Job"}`;
  /* ================= DATE HELPERS ================= */
  const getMaterialEstimatedCost = (material = {}) => {
    const qty = Number(material.qty || 0);
    const costPer = Number(material.costPer || 0);
    return qty * costPer;
  };

  const normalizeMaterial = (material = {}) => ({
    ...material,
    name: material.name || material.product || "",
    qty: material.qty ?? 1,
    unit: material.unit || "pcs",
    costPer: material.costPer ?? "",
    purchaseLocation: material.purchaseLocation || "",
    notes: material.notes || material.materialNotes || "",
    purchased: Boolean(material.purchased),
    files: Array.isArray(material.files) ? material.files : [],
  });

  const updateMaterialStatus = async (materialIndex, purchased) => {
    if (!selectedJob?.id) return;

    try {
      const groupId = getMaterialsGroupId(selectedJob);
      const currentMaterials = getMaterialsForJob(selectedJob);

      const nextMaterials = currentMaterials.map((material, index) =>
        index === materialIndex
          ? { ...material, purchased: Boolean(purchased) }
          : material
      );

      await saveMaterialsForJob(selectedJob, nextMaterials);

      setJobMaterials((previous) => ({
        ...previous,
        [groupId]: nextMaterials,
      }));

      setMaterialsDraft((previous) =>
        previous.map((material, index) =>
          index === materialIndex
            ? { ...material, purchased: Boolean(purchased) }
            : material
        )
      );
    } catch (error) {
      console.error("Error updating material purchase status:", error);
      alert("Could not update the material purchase status.");
    }
  };

  const uploadMaterialFiles = async (materialIndex, fileList) => {
    if (!selectedJob) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const groupId = getMaterialsGroupId(selectedJob);
    const uploaded = [];

    for (const file of files) {
      const fileRef = ref(
        getStorage(),
        `materialFiles/${groupId}/${Date.now()}_${file.name}`
      );
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      uploaded.push({ url, name: file.name });
    }

    setMaterialsDraft((previous) =>
      previous.map((material, index) =>
        index === materialIndex
          ? { ...material, files: [...(material.files || []), ...uploaded] }
          : material
      )
    );
  };

  const removeMaterialFile = (materialIndex, fileIndex) => {
    setMaterialsDraft((previous) =>
      previous.map((material, index) =>
        index === materialIndex
          ? {
              ...material,
              files: (material.files || []).filter((_, fi) => fi !== fileIndex),
            }
          : material
      )
    );
  };

  const saveMaterials = async () => {
    if (!selectedJob?.id) return;

    const groupId = getMaterialsGroupId(selectedJob);

    await saveMaterialsForJob(selectedJob, materialsDraft);

    setJobMaterials((previous) => ({
      ...previous,
      [groupId]: materialsDraft,
    }));

    setShowMaterialsPopup(false);
  };

  const addMaterial = () => {
    setMaterialsDraft((previous) => [
      ...previous,
      normalizeMaterial({}),
    ]);
  };
  
  const goToToday = () => setSelectedDate(new Date());

  const toggleDay = (dir) =>
    setSelectedDate((d) =>
      dir === "prev"
        ? moment(d).subtract(1, "day").toDate()
        : moment(d).add(1, "day").toDate()
    );

  const toggleWeek = (dir) =>
    setSelectedDate((d) =>
      dir === "prev"
        ? moment(d).subtract(7, "days").toDate()
        : moment(d).add(7, "days").toDate()
    );

    const toDateKey = (v) => {
      if (!v) return "";
      const m = moment(v?.toDate ? v.toDate() : v);
      return m.isValid() ? m.format("YYYY-MM-DD") : "";
    };
    
    const toMomentDate = (v) => {
      if (!v) return null;
      const m = moment(v?.toDate ? v.toDate() : v);
      return m.isValid() ? m.startOf("day") : null;
    };
    
    const getRecurrenceType = (job) => {
      if (job?.recurrence && typeof job.recurrence === "object") {
        return (job.recurrence.type || "none").toString().toLowerCase().trim();
      }
    
      return (
        job?.reccurance ??
        job?.recurring ??
        job?.repeat ??
        job?.recurrence ??
        "none"
      )
        .toString()
        .toLowerCase()
        .trim();
    };
  
  /* ================= FILTERING ================= */

  const baseJobs = useMemo(() => {
    const seen = new Map();
  
    [...jobs, ...locallyScheduledJobs].forEach((job) => {
      if (!job?.id) return;
  
      if (!seen.has(job.id)) {
        const cleanJob = { ...job };

        delete cleanJob.__renderDate;
        delete cleanJob.occurrenceDate;
        delete cleanJob.originalOccurrenceDate;
        delete cleanJob.isOccurrenceOverride;

        seen.set(job.id, cleanJob);
      }
    });
  
    return Array.from(seen.values());
  }, [jobs, locallyScheduledJobs]);

  const jobsForRange = (start, end) =>
    baseJobs.filter((job) => {
      const recurrenceType = getRecurrenceType(job);
  
      // recurring jobs should always be allowed through,
      // then expandJobIntoDays decides which occurrences render
      if (recurrenceType !== "none") return true;
  
      const startDate = toMomentDate(job.startDate);
      const endDate = toMomentDate(job.endDate || job.startDate);
      
      if (!startDate || !endDate) return false;
      endDate.endOf("day");
  
      return (
        startDate.isSameOrBefore(end, "day") &&
        endDate.isSameOrAfter(start, "day")
      );
    });

const getFilteredJobs = () => {
  if (view === "week" || view === "availability") {
    return jobsForRange(getWeekStart(selectedDate), getWeekEnd(selectedDate));
  }

  if (view === "day") {
    return jobsForRange(
      moment(selectedDate).startOf("day"),
      moment(selectedDate).endOf("day")
    );
  }

  if (view === "month") {
    return jobsForRange(
      moment(selectedDate).startOf("month"),
      moment(selectedDate).endOf("month")
    );
  }

  return jobs;
};

  /* ================= STYLES ================= */

  const jobStyle = (job) => ({
    background: getJobTileColor(job),
    color: "#fff",
    padding: 6,
    borderRadius: 6,
    marginBottom: 4,
    cursor: "pointer",
    border: "1px solid rgba(255,255,255,0.18)",
  });
  
  const completedTileStyle = {
    opacity: 0.95,
  };
  

  const timeOffStyle = (r) => ({
    background: "#dc2626",
    color: "#fff",
    padding: 6,
    borderRadius: 6,
    marginBottom: 4,
    cursor: "pointer",
  });

  const getNotesForSelectedJob = (job) => {
    if (!job) return [];

    if (isRecurringJob(job)) {
      const occurrenceKey = getOccurrenceDateKey(job);
      return job.occurrenceNotes?.[occurrenceKey] || [];
    }

    return Array.isArray(job.notes) ? job.notes : [];
  };

  /* ================= SAVE / NOTE LOGIC ================= */

  const markJobCompleted = async () => {
    if (!selectedJob) return;
  
    const jobRef = doc(db, "jobs", selectedJob.id);
    // Canonical (series) occurrence date, not the rescheduled day.
    const occurrenceKey = getCompletionOccurrenceKey(selectedJob);
  
    try {
      if (isRecurringJob(selectedJob)) {
        // ✅ Recurring: complete only THIS occurrence date
        await updateDoc(jobRef, {
          completedDates: arrayUnion(occurrenceKey),
          [`completedAtByDate.${occurrenceKey}`]: new Date().toISOString(),
          updatedAt: serverTimestamp(),
        });
  
        // UI: keep job, just close popups
        setShowVerificationPopup(false);
        setSelectedJob(null);

        // Reload so Home's job list, SendInvoices and Job History all pick
        // this up. View/date/tab are persisted, so we land right back here.
        refreshKeepingView();
        return;
      }
  
      // ✅ One-time: set status Completed AND also store the completion date
      await updateDoc(jobRef, {
        status: "Completed",
        completedDates: arrayUnion(occurrenceKey),
        completedAt: serverTimestamp(),
      });
  
      setShowVerificationPopup(false);
      setSelectedJob(null);

      refreshKeepingView();
  
      // Optional UI behavior:
      // - If your page hides Completed jobs, you can remove it from local state here.
      // - If you want it to still show (but crossed out), don’t filter it out.
    } catch (err) {
      console.error("Error marking job completed:", err);
      alert("Could not mark job completed. Please try again.");
    }
  };
  
  

  const saveQuickJobFields = async (changes) => {
    if (!selectedJob?.id) return;
    const result = await saveRecurringJobEdit({ originalJob: selectedJob, editedJob: { ...selectedJob, ...changes } });
    if (result.mode === "cancel") return false;
    const nextSelectedJob = result.mode === "future" ? result.newJob : result.updatedJob;
    if (typeof onJobUpdated === "function") {
      if (result.mode === "future") { onJobUpdated(result.oldJob, result); onJobUpdated(result.newJob, result); }
      else if (result.mode === "occurrence") onJobUpdated(result.masterJob, result);
      else onJobUpdated(result.updatedJob, result);
    }
    setSelectedJob(nextSelectedJob);
    setEditedJob(nextSelectedJob);
    return true;
  };

  const saveNote = async () => {
    if (
      !selectedJob ||
      (noteText.trim() === "" && selectedFiles.length === 0)
    ) {
      return;
    }

    setUploading(true);

    try {
      const jobId = selectedJob.id;
      const occurrenceKey = getOccurrenceDateKey(selectedJob);
      const recurring = isRecurringJob(selectedJob);

      let uploads = [];

      if (selectedFiles.length > 0) {
        uploads = await Promise.all(
          selectedFiles.map(async (file, index) => {
            const folderKey = recurring ? occurrenceKey : "main";
            const fileRef = ref(
              getStorage(),
              `notes/${jobId}/${folderKey}/${Date.now()}_${index}_${file.name}`
            );

            await uploadBytes(fileRef, file);

            return {
              fileURL: await getDownloadURL(fileRef),
              fileName: file.name,
            };
          })
        );
      }

      const newNote = {
        text: noteText.trim(),
        timestamp: new Date().toLocaleString(),
        author: userFullName || "User",
        files: uploads,
        occurrenceDate: recurring ? occurrenceKey : null,
      };

      const jobRef = doc(db, "jobs", jobId);
      const freshSnapshot = await getDoc(jobRef);
      const freshJob = freshSnapshot.exists() ? freshSnapshot.data() : {};

      let updatedJobData;
      let noteIndex;

      if (recurring) {
        const existingOccurrenceNotes = freshJob.occurrenceNotes || {};
        const notesForOccurrence =
          existingOccurrenceNotes[occurrenceKey] || [];

        const updatedNotesForOccurrence = [
          ...notesForOccurrence,
          newNote,
        ];

        const updatedOccurrenceNotes = {
          ...existingOccurrenceNotes,
          [occurrenceKey]: updatedNotesForOccurrence,
        };

        noteIndex = updatedNotesForOccurrence.length - 1;

        await updateDoc(jobRef, {
          occurrenceNotes: updatedOccurrenceNotes,
        });

        updatedJobData = {
          ...freshJob,
          occurrenceNotes: updatedOccurrenceNotes,
        };
      } else {
        const existingNotes = Array.isArray(freshJob.notes)
          ? freshJob.notes
          : [];

        const updatedNotes = [...existingNotes, newNote];
        noteIndex = updatedNotes.length - 1;

        await updateDoc(jobRef, {
          notes: updatedNotes,
        });

        updatedJobData = {
          ...freshJob,
          notes: updatedNotes,
        };
      }

      const refreshedSelectedJob = {
        ...selectedJob,
        ...updatedJobData,
        id: jobId,
        __renderDate: selectedJob.__renderDate,
        occurrenceDate: selectedJob.occurrenceDate,
      };

      // Immediately refresh the open popup.
      setSelectedJob(refreshedSelectedJob);

      // Push the updated job back into Home's jobs state.
      if (typeof onJobUpdated === "function") {
        onJobUpdated({
          id: jobId,
          ...updatedJobData,
        });
      }

      // Send the same job-note notification used by Home.
      try {
        await sendJobNoteEmailCallable({
          jobId,
          noteIndex,
          occurrenceDate: recurring ? occurrenceKey : null,
        });
      } catch (emailError) {
        console.error(
          "Note saved, but note email failed:",
          emailError
        );
      }

      setNoteText("");
      setSelectedFiles([]);
      setShowNotePopup(false);
    } catch (error) {
      console.error("Error saving note:", error);
      alert("There was an error saving your note. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const expandJobIntoDays = (job) => {
    const baseStart = toMomentDate(job.startDate);
    const baseEnd = toMomentDate(job.endDate || job.startDate);
    const recurrenceType = getRecurrenceType(job);
    
    if (!baseStart || !baseEnd) return [];
  
    const days = [];
  
    // ONE-TIME JOB
    if (recurrenceType === "none") {
      let current = baseStart.clone();
  
      while (current.isSameOrBefore(baseEnd, "day")) {
        days.push({
          ...job,
          __renderDate: current.format("YYYY-MM-DD"),
        });
        current.add(1, "day");
      }
  
      return days;
    }
  
    // RECURRING JOBS
    const windowStart =
    view === "day"
      ? moment(selectedDate).startOf("day")
      : view === "week" || view === "availability"
      ? getWeekStart(selectedDate)
      : moment(selectedDate).startOf("month").startOf("week");
  
  const rawWindowEnd =
    view === "day"
      ? moment(selectedDate).endOf("day")
      : view === "week" || view === "availability"
      ? getWeekEnd(selectedDate)
      : moment(selectedDate).endOf("month").endOf("week");

  // A series that has been ended (Close this job and all future jobs, or a
  // split "future" edit) must stop generating occurrences after that date -
  // otherwise it overlaps with whatever replaced it and shows as duplicate
  // tiles on the same date.
  const recurrenceEndKey = getRecurrenceEndKey(job);
  const windowEnd = recurrenceEndKey
    ? moment.min(rawWindowEnd, moment(recurrenceEndKey, "YYYY-MM-DD").endOf("day"))
    : rawWindowEnd;

  // Occurrences individually closed via "Close this job only (this date)".
  const excludedDates = getExcludedDateSet(job);
  
    // Recurring occurrences are single-day (this reached the "RECURRING
    // JOBS" branch above, so recurrenceType !== "none" here already).
    // endDate on a recurring job marks when the series stops repeating,
    // not a per-occurrence duration - using it as a span made every
    // occurrence overlap the next one's tiles and duplicate on shared days.
    const spanDays = 0;
  
    // CUSTOM DATES
    if (recurrenceType === "customdates") {
      (job.recurrence?.customDates || []).forEach((d) => {
        const occurrenceStart = moment(d).startOf("day");
        if (!occurrenceStart.isValid()) return;
        if (excludedDates.has(occurrenceStart.format("YYYY-MM-DD"))) return;
  
        for (let i = 0; i <= spanDays; i++) {
          const renderDay = occurrenceStart.clone().add(i, "days");
          if (renderDay.isBetween(windowStart, windowEnd, "day", "[]")) {
            days.push({
              ...job,
              originalOccurrenceDate: occurrenceStart.format("YYYY-MM-DD"),
              occurrenceDate: occurrenceStart.format("YYYY-MM-DD"),
              startDate: occurrenceStart.format("YYYY-MM-DD"),
              endDate: occurrenceStart.clone().add(spanDays, "days").format("YYYY-MM-DD"),
              __renderDate: renderDay.format("YYYY-MM-DD"),
            });
          }
        }
      });
  
      return days;
    }
  
    // WEEKLY with selected weekdays
    if (
      recurrenceType === "weekly" ||
      recurrenceType === "biweekly"
    ) {
      const intervalWeeks = recurrenceType === "biweekly" ? 2 : 1;
      const selectedDays =
      Array.isArray(job.recurrence?.daysOfWeek) && job.recurrence.daysOfWeek.length
        ? job.recurrence.daysOfWeek.map(Number)
        : [baseStart.day()];
  
      let cursor = windowStart.clone();
  
      while (cursor.isSameOrBefore(windowEnd, "day")) {
        const weeksDiff = cursor.clone().startOf("day").diff(baseStart.clone().startOf("week"), "weeks");
        const inCycle = weeksDiff >= 0 && weeksDiff % intervalWeeks === 0;
  
        if (
          inCycle &&
          selectedDays.includes(cursor.day()) &&
          cursor.isSameOrAfter(baseStart, "day") &&
          !excludedDates.has(cursor.format("YYYY-MM-DD"))
        ) {
          for (let i = 0; i <= spanDays; i++) {
            const renderDay = cursor.clone().add(i, "days");
            if (renderDay.isBetween(windowStart, windowEnd, "day", "[]")) {
              days.push({
                ...job,
                originalOccurrenceDate: cursor.format("YYYY-MM-DD"),
                occurrenceDate: cursor.format("YYYY-MM-DD"),
                startDate: cursor.format("YYYY-MM-DD"),
                endDate: cursor.clone().add(spanDays, "days").format("YYYY-MM-DD"),
                __renderDate: renderDay.format("YYYY-MM-DD"),
              });
            }
          }
        }
  
        cursor.add(1, "day");
      }
  
      return days;
    }
  
    // MONTH / BIMONTHLY / QUARTERLY / BIYEARLY / YEARLY / DAILY
    let cursor = baseStart.clone();
  
    while (cursor.isSameOrBefore(windowEnd, "day")) {
      if (
        cursor.isSameOrAfter(windowStart, "day") &&
        !excludedDates.has(cursor.format("YYYY-MM-DD"))
      ) {
        for (let i = 0; i <= spanDays; i++) {
          const renderDay = cursor.clone().add(i, "days");
          if (renderDay.isBetween(windowStart, windowEnd, "day", "[]")) {
            days.push({
              ...job,
              originalOccurrenceDate: cursor.format("YYYY-MM-DD"),
              occurrenceDate: cursor.format("YYYY-MM-DD"),
              startDate: cursor.format("YYYY-MM-DD"),
              endDate: cursor.clone().add(spanDays, "days").format("YYYY-MM-DD"),
              __renderDate: renderDay.format("YYYY-MM-DD"),
            });
          }
        }
      }
  
      if (recurrenceType === "daily") {
        cursor.add(1, "day");
      } else if (recurrenceType === "monthly") {
        cursor.add(1, "month");
      } else if (recurrenceType === "bimonthly") {
        cursor.add(2, "months");
      } else if (recurrenceType === "quarterly") {
        cursor.add(3, "months");
      } else if (recurrenceType === "biyearly") {
        cursor.add(6, "months");
      } else if (recurrenceType === "yearly") {
        cursor.add(1, "year");
      } else {
        break;
      }
    }
  
    return days;
  };
  
  const expandedJobs = useMemo(() => {
    const normalOccurrences = getFilteredJobs()
      .flatMap(expandJobIntoDays)
      .map(applyOccurrenceOverrideToRenderedJob);

    const movedOccurrences = baseJobs.flatMap(
      getOccurrenceOverrideRenderJobs
    );

    return mergeRenderedRecurringJobs([
      ...normalOccurrences,
      ...movedOccurrences,
    ]);
  }, [baseJobs, selectedDate, view]);
  
  const jobsByDate = useMemo(() => {
    const map = {};
    const seen = new Set();
  
    const techFilter = String(onlyTechName || "").trim();
    expandedJobs.forEach((job) => {
      const dateKey = job.__renderDate;
      if (!dateKey) return;

      // Tech view: only occurrences this tech is assigned to.
      if (techFilter) {
        const assigned = Array.isArray(job.techAssign)
          ? job.techAssign
          : [job.techAssign];
        if (!assigned.includes(techFilter)) return;
      }

      const recurring = isRecurringJob(job);
      const occurrenceKey = toDateKey(
        job.occurrenceDate || job.__renderDate || job.startDate
      );
      const recurringSeriesKey = [
        job.wo || job.workOrder || job.workOrderNumber || "",
        job.siteName || job.property || "",
        job.serviceType || "",
        toDateKey(job.originalSeriesStartDate || job.seriesStartDate || job.startDate),
        getRecurrenceType(job),
      ]
        .map((value) => String(value || "").trim().toLowerCase())
        .join("|");

      const sourceKey = recurring
        ? recurringSeriesKey || String(job.id || "job").trim().toLowerCase()
        : String(job.id || job.wo || "job").trim().toLowerCase();

      const originalOccurrenceKey = toDateKey(
        job.originalOccurrenceDate || job.occurrenceDate || job.__renderDate || job.startDate
      );
      const dedupeKey = `${sourceKey}_${originalOccurrenceKey || occurrenceKey}_${dateKey}`;

      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
  
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(job);
    });
  
    return map;
  }, [expandedJobs, onlyTechName]);

  // This page loaded time off but never drew it. Same map as Calendar.js.
  const timeOffByDate = useMemo(
    () => buildTimeOffByDate(timeOffRequests),
    [timeOffRequests]
  );
  
  const getJobRenderKey = (job, index = 0) =>
    [
      job.id,
      job.__renderDate || "",
      job.occurrenceDate || "",
      job.startDate || "",
      job.endDate || "",
      job.wo || "",
      index,
    ].join("_");


  const normalizeTechAssign = (value) => {
    if (Array.isArray(value)) return value.length ? value : [""];
    if (typeof value === "string" && value.trim()) {
      return value
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean);
    }
    return [""];
  };

  const cleanTechAssign = (value) =>
    normalizeTechAssign(value).map((name) => name.trim()).filter(Boolean);

  const addEditedTechnicianField = () => {
    setEditedJob((previous) => ({
      ...previous,
      techAssign: [...normalizeTechAssign(previous.techAssign), ""],
    }));
  };

  const removeEditedTechnicianField = (indexToRemove) => {
    setEditedJob((previous) => {
      const nextTechs = normalizeTechAssign(previous.techAssign).filter(
        (_, index) => index !== indexToRemove
      );

      return {
        ...previous,
        techAssign: nextTechs.length ? nextTechs : [""],
      };
    });
  };

  const updateEditedTechnicianField = (indexToUpdate, value) => {
    setEditedJob((previous) => {
      const nextTechs = normalizeTechAssign(previous.techAssign);
      nextTechs[indexToUpdate] = value;

      return {
        ...previous,
        techAssign: nextTechs,
      };
    });
  };

  const openInternalEdit = (job) => {
    if (!job) return;

    setEditedJob({
      ...job,
      techAssign: normalizeTechAssign(job.techAssign),
      startTimeMode:
        job.startTime && /^\d{2}:\d{2}$/.test(job.startTime)
          ? "custom"
          : "preset",
      jobPlan: {
        estimatedDays: job.jobPlan?.estimatedDays || "",
        estimatedLabor: job.jobPlan?.estimatedLabor || "",
      },
    });

    setIsEditing(true);
  };

  const handleEditInputChange = (event) => {
    const { name, value } = event.target;
    setEditedJob((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleEditSiteChange = (event) => {
    const siteName = event.target.value;
    const property = propertiesList.find(
      (item) =>
        item.propertyName === siteName || item.siteName === siteName
    );

    setEditedJob((previous) => ({
      ...previous,
      siteName,
      address: property?.address || previous.address || "",
      city: property?.city || previous.city || "",
      state: property?.state || previous.state || "",
      zip: property?.zip || previous.zip || "",
    }));
  };

  const saveEmbeddedJobChanges = async () => {
    if (!selectedJob?.id || !editedJob?.id) return;

    try {
      const jobToSave = {
        ...editedJob,
        techAssign: cleanTechAssign(editedJob.techAssign),
      };

      const result = await saveRecurringJobEdit({
        originalJob: selectedJob,
        editedJob: jobToSave,
      });

      if (result.mode === "cancel") return;

      let updatedForParent = null;

      if (result.mode === "future") {
        updatedForParent = result.newJob;
        setSelectedJob(result.newJob);
      } else if (result.mode === "occurrence") {
        updatedForParent = result.masterJob;
        setSelectedJob(result.updatedJob);
      } else {
        updatedForParent = result.updatedJob;
        setSelectedJob(result.updatedJob);
      }

      if (typeof onJobUpdated === "function" && updatedForParent) {
        onJobUpdated(updatedForParent, result);
      }

      setIsEditing(false);
      setEditedJob({});
    } catch (error) {
      console.error("Error saving calendar job changes:", error);
      alert("Could not save job changes.");
    }
  };

  /* ================= RENDER ================= */

  return (
    <div>
      <div
  style={{
    display: "flex",
    gap: 6,
    marginBottom: 12,
    borderBottom: "1px solid #1d4ed8",
  }}
>
 

  {/* Month */}
  <button
  onClick={() => setView("month")}
  style={{
    padding: "10px 14px",
    borderRadius: "10px 10px 0 0",
    border: view === "month"
      ? "1px solid #1d4ed8"
      : "1px solid #FB923C",
    borderBottom: view === "month" ? "none" : "1px solid #FB923C",
    background: view === "month" ? "#fff" : "#FED7AA",
    color: view === "month" ? "#0f172a" : "#7C2D12",
    fontWeight: 700,
    cursor: "pointer",
  }}
>
  Month
</button>


  {/* Week */}
  <button
  onClick={() => setView("week")}
  style={{
    padding: "10px 14px",
    borderRadius: "10px 10px 0 0",
    border: view === "week"
      ? "1px solid #1d4ed8"
      : "1px solid #FB923C",
    borderBottom: view === "week" ? "none" : "1px solid #FB923C",
    background: view === "week" ? "#fff" : "#FED7AA",
    color: view === "week" ? "#0f172a" : "#7C2D12",
    fontWeight: 700,
    cursor: "pointer",
  }}
>
  Week
</button>


  {/* Day */}
  <button
  onClick={() => setView("day")}
  style={{
    padding: "10px 14px",
    borderRadius: "10px 10px 0 0",
    border: view === "day"
      ? "1px solid #1d4ed8"
      : "1px solid #FB923C",
    borderBottom: view === "day" ? "none" : "1px solid #FB923C",
    background: view === "day" ? "#fff" : "#FED7AA",
    color: view === "day" ? "#0f172a" : "#7C2D12",
    fontWeight: 700,
    cursor: "pointer",
  }}
>
  Day
</button>

  {/* Availability (office staff only: it schedules jobs) */}
  {isStaffRole && (
  <button
  onClick={() => setView("availability")}
  style={{
    padding: "10px 14px",
    borderRadius: "10px 10px 0 0",
    border: view === "availability"
      ? "1px solid #1d4ed8"
      : "1px solid #FB923C",
    borderBottom: view === "availability" ? "none" : "1px solid #FB923C",
    background: view === "availability" ? "#fff" : "#FED7AA",
    color: view === "availability" ? "#0f172a" : "#7C2D12",
    fontWeight: 700,
    cursor: "pointer",
  }}
>
  Availability
</button>
  )}


  {/* Today (utility, not a tab) */}
  <button
    onClick={goToToday}
    style={{
      marginLeft: "auto",
      padding: "8px 12px",
      borderRadius: 8,
      border: "1px solid #1d4ed8",
      background: "#FFF7ED",
      fontWeight: 600,
      cursor: "pointer",
      color: "#7C2D12",
    }}
  >
    Today
  </button>
</div>

<div
  style={{
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
    padding: "12px 14px",
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 10,
    alignItems: "center",
  }}
>
  <strong style={{ color: "#7c2d12", marginRight: 6 }}>Calendar Legend:</strong>

  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span style={{ width: 14, height: 14, borderRadius: 4, background: "#6FAE7C", display: "inline-block" }} />
    <span>Proposal</span>
  </div>

  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span style={{ width: 14, height: 14, borderRadius: 4, background: "#D9B95B", display: "inline-block" }} />
    <span>Meeting / Inspection</span>
  </div>

  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span style={{ width: 14, height: 14, borderRadius: 4, background: "#8E6BBE", display: "inline-block" }} />
    <span>Action Item</span>
  </div>

  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span style={{ width: 14, height: 14, borderRadius: 4, background: "#5B8FD9", display: "inline-block" }} />
    <span>All Other Jobs</span>
  </div>

  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span style={{ width: 14, height: 14, borderRadius: 4, background: "#D96C6C", display: "inline-block" }} />
    <span>Completed / Invoice Sent</span>
  </div>

  <TimeOffLegendItems />
</div>

{view === "month" && (
  <Calendar
    value={selectedDate}
    onChange={setSelectedDate}
    calendarType="gregory"
          tileContent={({ date, view }) =>
            view === "month" && (
            <>
         {(jobsByDate[moment(date).format("YYYY-MM-DD")] || []).map((j, index) => (
    <div
    key={getJobRenderKey(j, index)}
      style={{
        ...jobStyle(j),
        ...(isClosedJob(j) ? completedTileStyle : {}),
      }}
      onClick={() => setSelectedJob(j)}
    >
      {j.wo && (
        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}>
          WO# {j.wo}
        </div>
      )}
      {getJobDisplayText(j)}
{j.jobPlan?.estimatedDays && j.jobPlan?.estimatedLabor && (
  <div style={{ fontSize: 11, opacity: 0.9 }}>
    ⏱ {j.jobPlan.estimatedDays}d · 👷 {j.jobPlan.estimatedLabor}
  </div>
)}

            </div>
          ))}
          {(timeOffByDate[moment(date).format("YYYY-MM-DD")] || []).map((r) => (
            <TimeOffChip key={r.id} entry={r} onOpen={setSelectedTimeOffRequest} />
          ))}
            </>
            )
          }
        />
      )}

{view === "week" &&
  Array.from({ length: 7 }, (_, i) =>
    getWeekStart(selectedDate).clone().add(i, "days").format("YYYY-MM-DD")
  ).map((dateKey) => {
    const jobsForDay = jobsByDate[dateKey] || [];

    return (
      <div key={dateKey} style={{ border: "1px solid #ddd", marginBottom: 12, padding: 8, borderRadius: 8 }}>
        <h3>{moment(dateKey).format("ddd, MMM DD")}</h3>

        {jobsForDay.map((j, index) => (
          <div
          key={getJobRenderKey(j, index)}
            style={{
              ...jobStyle(j),
              ...(isClosedJob(j) ? completedTileStyle : {}),
            }}
            onClick={() => setSelectedJob(j)}
          >
            <strong>WO# {j.wo || "—"}:</strong> {getJobDisplayText(j)} — {j.city}
          </div>
        ))}

        {(timeOffByDate[dateKey] || []).map((r) => (
          <TimeOffChip key={r.id} entry={r} onOpen={setSelectedTimeOffRequest} />
        ))}
      </div>
    );
  })}

{view === "day" && (
  <OutTodayList
    entries={timeOffByDate[moment(selectedDate).format("YYYY-MM-DD")] || []}
    onOpen={setSelectedTimeOffRequest}
  />
)}

{view === "day" &&
  (jobsByDate[moment(selectedDate).format("YYYY-MM-DD")] || []).map((j, index) => (
      <div
      key={getJobRenderKey(j, index)}
        style={{
          ...jobStyle(j),
          ...(isClosedJob(j) ? completedTileStyle : {}),
        }}
        onClick={() => setSelectedJob(j)}
      >
        <strong>WO# {j.wo || "—"}:</strong> {getJobDisplayText(j)} ({j.startTime || "ANY"})
      </div>
    ))}

{view === "availability" && isStaffRole && (
  <AvailabilityView
    selectedDate={selectedDate}
    onSelectDate={setSelectedDate}
    jobsByDate={jobsByDate}
    timeOffByDate={timeOffByDate}
    usersList={usersList}
    onOpenJob={(job) => job && setSelectedJob(job)}
    onScheduleSlot={({ dateKey, startTime, techName }) =>
      setScheduleDraft({
        startDate: dateKey,
        endDate: dateKey,
        startTime,
        techAssign: techName ? [techName] : [],
      })
    }
  />
)}

{/* Schedule Job popup — the real Scheduling form, opened from an open block */}
{scheduleDraft && (
  <Scheduling
    key={`${scheduleDraft.startDate}-${scheduleDraft.startTime}-${(scheduleDraft.techAssign || []).join(",")}`}
    scheduleOnly
    prefill={scheduleDraft}
    onClose={() => setScheduleDraft(null)}
    onScheduled={(newJob) => {
      setLocallyScheduledJobs((prev) => [...prev.filter((j) => j.id !== newJob.id), newJob]);
      if (onJobUpdated) onJobUpdated(newJob);
    }}
  />
)}


      {/* ================= INTERNAL EDIT MODAL ================= */}
      {selectedJob && isEditing && createPortal(
        <>
          <div
            onClick={() => {
              setIsEditing(false);
              setEditedJob({});
            }}
            style={{
              position: "fixed",
              inset: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.55)",
              zIndex: 999998,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              margin: 0,
              width: "90%",
              maxWidth: 560,
              maxHeight: "85vh",
              overflowY: "auto",
              background: "#fff",
              padding: 20,
              borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
              textAlign: "left",
              boxSizing: "border-box",
              zIndex: 999999,
            }}
          >
            <h2>Edit Job</h2>

            <div className="form-grid">
              <label>
                Site Name
                <select
                  name="siteName"
                  value={editedJob.siteName || ""}
                  onChange={handleEditSiteChange}
                >
                  <option value="">Select Site</option>
                  {[...propertiesList]
                    .sort((a, b) =>
                      String(a.propertyName || a.siteName || "").localeCompare(
                        String(b.propertyName || b.siteName || "")
                      )
                    )
                    .map((property) => {
                      const label =
                        property.propertyName || property.siteName || "";
                      return (
                        <option key={property.id} value={label}>
                          {label}
                        </option>
                      );
                    })}
                </select>
              </label>

              <label>
                Address
                <input
                  name="address"
                  value={editedJob.address || ""}
                  onChange={handleEditInputChange}
                />
              </label>

              <label>
                City
                <input
                  name="city"
                  value={editedJob.city || ""}
                  onChange={handleEditInputChange}
                />
              </label>

              <label>
                State
                <input
                  name="state"
                  value={editedJob.state || ""}
                  onChange={handleEditInputChange}
                />
              </label>

              <label>
                Start Date
                <input
                  type="date"
                  name="startDate"
                  value={editedJob.startDate || ""}
                  onChange={handleEditInputChange}
                />
              </label>

              <label>
                End Date
                <input
                  type="date"
                  name="endDate"
                  value={editedJob.endDate || ""}
                  onChange={handleEditInputChange}
                />
              </label>

              <label>
                Start Time
                <select
                  value={
                    editedJob.startTimeMode === "custom"
                      ? "Custom Time"
                      : editedJob.startTime || ""
                  }
                  onChange={(event) => {
                    const value = event.target.value;

                    if (value === "Custom Time") {
                      setEditedJob((previous) => ({
                        ...previous,
                        startTimeMode: "custom",
                        startTime: "",
                      }));
                    } else {
                      setEditedJob((previous) => ({
                        ...previous,
                        startTimeMode: "preset",
                        startTime: value,
                      }));
                    }
                  }}
                >
                  <option value="">Select Start Time</option>
                  {timeWindowOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              {editedJob.startTimeMode === "custom" && (
                <label>
                  Custom Start Time
                  <input
                    type="time"
                    value={editedJob.startTime || ""}
                    onChange={(event) =>
                      setEditedJob((previous) => ({
                        ...previous,
                        startTime: event.target.value,
                      }))
                    }
                  />
                </label>
              )}

              <label>
                End Time
                <input
                  type="time"
                  name="endTime"
                  value={editedJob.endTime || ""}
                  onChange={handleEditInputChange}
                />
              </label>

              <label>
                Service Type
                <select
                  name="serviceType"
                  value={editedJob.serviceType || ""}
                  onChange={handleEditInputChange}
                >
                  <option value="">Select Service Type</option>
                  {[
                    "Proposal",
                    "Meeting",
                    "Construction",
                    "Maintenance",
                    "Action Items",
                    "Cleaning",
                    "Bulk Trash",
                    "Inspection",
                    "Electrical",
                    "Plumbing",
                    "HVAC",
                    "Concrete",
                  ].map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Scope
                <input
                  name="scope"
                  value={editedJob.scope || ""}
                  onChange={handleEditInputChange}
                />
              </label>

              <label>
                Job Description
                <textarea
                  name="jobDescription"
                  value={editedJob.jobDescription || ""}
                  onChange={handleEditInputChange}
                />
              </label>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
                  Tech Assign
                </label>

                {normalizeTechAssign(editedJob.techAssign).map((tech, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <select
                      value={tech}
                      onChange={(event) =>
                        updateEditedTechnicianField(index, event.target.value)
                      }
                      style={{ flex: 1 }}
                    >
                      <option value="">Select Technician</option>
                      {usersList.map((user) => {
                        const name = `${user.firstName || ""} ${
                          user.lastName || ""
                        }`.trim();

                        return (
                          <option key={user.id} value={name}>
                            {name}
                          </option>
                        );
                      })}
                    </select>

                    <button
                      type="button"
                      className="red-button"
                      onClick={() => removeEditedTechnicianField(index)}
                      style={{
                        width: 44,
                        minWidth: 44,
                        height: 44,
                        padding: 0,
                        fontSize: 18,
                        fontWeight: "bold",
                      }}
                      title="Remove technician"
                    >
                      X
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="blue-button"
                  onClick={addEditedTechnicianField}
                >
                  Add Technician
                </button>
              </div>

              <label>
                Status
                <select
                  name="status"
                  value={editedJob.status || "Active"}
                  onChange={handleEditInputChange}
                >
                  {[
                    "Active",
                    "On Hold",
                    "Completed",
                    "Invoice Sent",
                    "Monthly",
                  ].map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="modal-buttons">
              <button
                type="button"
                className="green-button"
                onClick={saveEmbeddedJobChanges}
              >
                Save Changes
              </button>

              <button
                type="button"
                className="red-button"
                onClick={() => {
                  setIsEditing(false);
                  setEditedJob({});
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* ================= JOB MODAL ================= */}
      {selectedJob && !isEditing && createPortal(
        <>
          <div
            onClick={() => setSelectedJob(null)}
            style={{
              position: "fixed",
              inset: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.55)",
              zIndex: 999998,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              margin: 0,
              width: "min(1000px, calc(100vw - 40px))",
              maxHeight: "85vh",
              overflowY: "auto",
              background: "#fff",
              padding: 20,
              borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
              textAlign: "left",
              boxSizing: "border-box",
              zIndex: 999999,
            }}
          >
  <h2>Job Details</h2>

  {["Admin", "Manager", "Assistant"].includes(userRole) && (
    <QuickJobFieldEditor job={selectedJob} usersList={usersList} onSave={saveQuickJobFields} />
  )}

  {jobVisits &&
    (userRole === "Tech" ||
      (userFullName &&
        (Array.isArray(selectedJob.techAssign)
          ? selectedJob.techAssign.includes(userFullName)
          : selectedJob.techAssign === userFullName))) && (
      <JobCheckInButton job={selectedJob} jobVisits={jobVisits} />
    )}
  {isStaffRole && <JobVisitHistory job={selectedJob} />}

  <p><strong>Site Name:</strong> {selectedJob.siteName || "—"}</p>
  <p><strong>Work Order:</strong> {selectedJob.wo || "—"}</p>

  <p><strong>Address:</strong> {selectedJob.address || "—"}</p>
  <p><strong>City:</strong> {selectedJob.city || "—"}</p>
  <p><strong>State:</strong> {selectedJob.state || "—"}</p>

  <p>
    <strong>Start Date:</strong>{" "}
    {selectedJob.startDate
      ? moment(selectedJob.startDate).format("MM/DD/YYYY")
      : "—"}
  </p>

  <p>
    <strong>End Date:</strong>{" "}
    {selectedJob.endDate
      ? moment(selectedJob.endDate).format("MM/DD/YYYY")
      : "—"}
  </p>

  <p>
    <strong>Start Time:</strong>{" "}
    {selectedJob.startTime || "ANY"}
  </p>

  <p>
    <strong>End Time:</strong>{" "}
    {selectedJob.endTime || "—"}
  </p>

  <p>
    <strong>Service Type:</strong>{" "}
    {selectedJob.serviceType || "—"}
  </p>

  <p>
    <strong>Scope:</strong>{" "}
    {selectedJob.scope || "—"}
  </p>

  <p>
    <strong>Job Description:</strong>{" "}
    {selectedJob.jobDescription || "—"}
  </p>
  {(selectedJob.jobPlan?.estimatedDays ||
  selectedJob.jobPlan?.estimatedLabor) && (
  <p>
    <strong>Planned:</strong>{" "}
    {selectedJob.jobPlan?.estimatedDays
      ? `${selectedJob.jobPlan.estimatedDays} day(s)`
      : "—"}
    {" · "}
    {selectedJob.jobPlan?.estimatedLabor
      ? `${selectedJob.jobPlan.estimatedLabor} people`
      : "—"}
  </p>
)}

  <p>
    <strong>Client Phone:</strong>{" "}
    {selectedJob.clientPhone || "—"}
  </p>

  <p>
    <strong>Client Email:</strong>{" "}
    {selectedJob.clientEmail || "—"}
  </p>

  <p>
    <strong>Tech Assign:</strong>{" "}
    {Array.isArray(selectedJob.techAssign)
      ? selectedJob.techAssign.join(", ")
      : selectedJob.techAssign || "Unassigned"}
  </p>

  <p>
    <strong>Status:</strong>{" "}
    {selectedJob.status || "—"}
  </p>
{/* ===== Materials ===== */}
<h3 style={{ marginTop: 16 }}>Materials</h3>

{getMaterialsForJob(selectedJob).length > 0 ? (
  <div style={{ display: "grid", gap: 10 }}>
    {getMaterialsForJob(selectedJob).map((material, index) => {
      const estimatedCost = getMaterialEstimatedCost(material);

      return (
        <div
          key={index}
          style={{
            display: "grid",
            gridTemplateColumns: "42px minmax(0, 1fr)",
            gap: 10,
            alignItems: "start",
            padding: 12,
            border: material.purchased
              ? "2px solid #16a34a"
              : "1px solid #cbd5e1",
            borderRadius: 10,
            background: material.purchased ? "#f0fdf4" : "#f8fafc",
          }}
        >
          <button
            type="button"
            onClick={() => updateMaterialStatus(index, !material.purchased)}
            aria-label={
              material.purchased
                ? "Mark material as not purchased"
                : "Mark material as purchased"
            }
            title={
              material.purchased
                ? "Purchased — click to undo"
                : "Not purchased — click when purchased"
            }
            className={`material-toggle${material.purchased ? " material-toggle--checked" : ""}`}
          >
            <svg
              className="material-toggle__check"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="4 12 9 17 20 6" />
            </svg>
          </button>

          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 900, fontSize: 16 }}>
              {material.name || material.product || "Unnamed Material"}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 6,
                marginTop: 8,
                fontSize: 14,
              }}
            >
              <div>
                <strong>Qty:</strong> {material.qty ?? ""}
                {material.unit ? ` ${material.unit}` : ""}
              </div>
              <div>
                <strong>Cost Per:</strong>{" "}
                {Number(material.costPer || 0).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
              <div>
                <strong>Estimated Material Cost:</strong>{" "}
                {estimatedCost.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
              <div>
                <strong>Purchase Location:</strong>{" "}
                {material.purchaseLocation || "Not entered"}
              </div>
              <div>
                <strong>Status:</strong>{" "}
                {material.purchased ? "Purchased" : "Not Purchased"}
              </div>
            </div>

            {material.notes && (
              <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                <strong>Material Notes:</strong> {material.notes}
              </div>
            )}

            {Array.isArray(material.files) && material.files.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                {material.files.map((file, fileIndex) => (
                  <a
                    key={fileIndex}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={file.name}
                  >
                    {/\.(jpe?g|png|gif|webp)$/i.test(file.name || "") ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid #cbd5e1",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 60,
                          height: 60,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                          background: "#fff",
                          fontSize: 10,
                          textAlign: "center",
                          padding: 4,
                          overflow: "hidden",
                        }}
                      >
                        {file.name}
                      </div>
                    )}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    })}

    <div
      style={{
        padding: 10,
        borderRadius: 8,
        background: "#eff6ff",
        border: "1px solid #93c5fd",
        fontWeight: 900,
      }}
    >
      Total Estimated Material Cost:{" "}
      {getMaterialsForJob(selectedJob)
        .reduce(
          (sum, material) => sum + getMaterialEstimatedCost(material),
          0
        )
        .toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })}
    </div>
  </div>
) : (
  <p style={{ fontStyle: "italic" }}>No materials listed.</p>
)}


  <h3 style={{ marginTop: 16 }}>Notes</h3>

{getNotesForSelectedJob(selectedJob).length > 0 ? (
  <ul style={{ paddingLeft: 16 }}>
    {getNotesForSelectedJob(selectedJob).map((note, index) => (
      <li key={`${note.timestamp || "note"}-${index}`} style={{ marginBottom: 14 }}>
        <strong>
          {note.author} ({note.timestamp})
        </strong>

        {note.text && (
          <div style={{ marginTop: 4 }}>
            {note.text}
          </div>
        )}

        {/* 📎 FILE ATTACHMENTS */}
        {Array.isArray(note.files) && note.files.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {note.files.map((file, fileIndex) => {
              const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file.fileURL);

              return (
                <div key={fileIndex} style={{ marginBottom: 6 }}>
                  <a
                    href={file.fileURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#2563eb", fontWeight: 600 }}
                  >
                    {isImage ? (
                      <img
                        src={file.fileURL}
                        alt={file.fileName || `Attachment ${fileIndex + 1}`}
                        style={{
                          maxWidth: 120,
                          borderRadius: 6,
                          border: "1px solid #e5e7eb",
                          display: "block",
                        }}
                      />
                    ) : (
                      `View File ${fileIndex + 1}`
                    )}
                  </a>

                  {file.fileName && (
                    <div style={{ fontSize: 12, color: "#475569" }}>
                      {file.fileName}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </li>
    ))}
  </ul>
) : (
  <p>No notes yet.</p>
)}


  <CollapsibleActionSection title="Supervisor" tone="supervisor" defaultOpen={false}>
    {["Admin", "Manager", "Assistant"].includes(userRole) && (
      <button
        className="grey-button"
        onClick={() => {
          if (typeof onEditJob === "function") {
            onEditJob(selectedJob);
          } else {
            openInternalEdit(selectedJob);
          }
        }}
      >
        Edit
      </button>
    )}

    {["Admin", "Manager", "Assistant"].includes(userRole) && (
      <button
        className="grey-button"
        onClick={() => {
          if (typeof onAddSupervisorNote === "function") {
            onAddSupervisorNote(selectedJob);
          }
        }}
      >
        Add Supervisor Note
      </button>
    )}

    {!isRecurringJob(selectedJob) && selectedJob.status !== "Completed" && (
      <button
        className="yellow-button"
        onClick={() => {
          if (typeof onContinueJob === "function") {
            onContinueJob(selectedJob);
            setSelectedJob(null);
          }
        }}
      >
        Continue
      </button>
    )}

    <button
      className="green-button"
      onClick={() => {
        if (typeof onAddTotal === "function") {
          onAddTotal(selectedJob);
          setSelectedJob(null);
        }
      }}
    >
      Add / Change Total
    </button>

    {["Admin", "Manager"].includes(userRole) && (
      <button
        className="black-button"
        onClick={async () => {
          try {
            const result = await closeOrDeleteJob({ job: selectedJob });

            if (result.mode === "cancel") return;

            if (result.mode === "deleted") {
              if (typeof onJobUpdated === "function") {
                onJobUpdated({ id: result.jobId, __deleted: true });
              }
              setSelectedJob(null);
              return;
            }

            // "occurrence" or "future" - job still exists, just updated.
            if (typeof onJobUpdated === "function") {
              onJobUpdated(result.updatedJob, result);
            }
            setSelectedJob(null);
          } catch (error) {
            console.error("Error closing job:", error);
            alert("Could not close this job. Please try again.");
          }
        }}
      >
        Close Job
      </button>
    )}
  </CollapsibleActionSection>

  <div className="modal-buttons">
    <button
      className="blue-button"
      onClick={() => setShowNotePopup(true)}
    >
      Add Note
    </button>

    <button
      className="green-button"
      onClick={() => {
        if (typeof canCompleteJob === "function" && !canCompleteJob(selectedJob)) return;
        // Completing a job opens the Complete Job & Create Invoice form.
        setShowInvoiceModal(true);
      }}
    >
      Mark Job as Completed
    </button>
  </div>

  <div className="modal-buttons">
    <button
      className="blue-button"
      onClick={() => {
        if (typeof onAddReceipt === "function") {
          onAddReceipt(selectedJob);
          setSelectedJob(null);
        }
      }}
    >
      Add Receipt
    </button>

    <button
      className="blue-button"
      onClick={() => setShowMaterialsPopup(true)}
    >
      Add / Edit Materials
    </button>

    <button
      className="grey-button"
      onClick={() => setShowHistoryPopup(true)}
    >
      History
    </button>

    <button
      className="yellow-button"
      onClick={() => {
        if (typeof onChecklist === "function") {
          onChecklist(selectedJob);
        }
      }}
    >
      Checklist
    </button>

    <button
      className="red-button"
      onClick={() => setSelectedJob(null)}
    >
      Close
    </button>
  </div>
</div>

        </>,
        document.body
      )}

      {showHistoryPopup && (
        <JobHistoryPopup
          job={selectedJob}
          jobs={jobs}
          onClose={() => setShowHistoryPopup(false)}
        />
      )}

      {/* ================= NOTE POPUP ================= */}
      {showMaterialsPopup && createPortal(
  <>
    <div
      onClick={() => setShowMaterialsPopup(false)}
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.55)", zIndex: 1999998,
      }}
    />
    <div
      style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
        width: "min(1050px, 96vw)",
        maxHeight: "calc(100vh - 40px)",
        overflowY: "auto",
        background: "#fff", padding: 20, borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
        boxSizing: "border-box", zIndex: 1999999,
      }}
    >
      <h3 style={{ marginTop: 0 }}>Add / Edit Materials</h3>

      {materialsDraft.length === 0 && (
        <p style={{ fontStyle: "italic" }}>No materials have been added.</p>
      )}

      <div style={{ display: "grid", gap: 14 }}>
        {materialsDraft.map((material, index) => {
          const estimatedCost = getMaterialEstimatedCost(material);

          const updateDraftMaterial = (patch) => {
            setMaterialsDraft((previous) =>
              previous.map((item, itemIndex) =>
                itemIndex === index ? { ...item, ...patch } : item
              )
            );
          };

          return (
            <div
              key={index}
              style={{
                padding: 14,
                border: material.purchased
                  ? "2px solid #16a34a"
                  : "1px solid #cbd5e1",
                borderRadius: 12,
                background: material.purchased ? "#f0fdf4" : "#f8fafc",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <strong>Material {index + 1}</strong>
                <button
                  type="button"
                  className="red-button"
                  onClick={() =>
                    setMaterialsDraft((previous) =>
                      previous.filter((_, itemIndex) => itemIndex !== index)
                    )
                  }
                  style={{ width: "auto" }}
                >
                  Remove Material
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(190px, 1fr))",
                  gap: 12,
                }}
              >
                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Product
                  <input
                    type="text"
                    value={material.name || ""}
                    onChange={(event) =>
                      updateDraftMaterial({ name: event.target.value })
                    }
                    placeholder="Product or material name"
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Qty
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={material.qty ?? ""}
                    onChange={(event) =>
                      updateDraftMaterial({ qty: event.target.value })
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Unit
                  <input
                    type="text"
                    value={material.unit || ""}
                    onChange={(event) =>
                      updateDraftMaterial({ unit: event.target.value })
                    }
                    placeholder="pcs, ft, bags"
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Cost Per
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #cbd5e1",
                      borderRadius: 6,
                      background: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <span style={{ paddingLeft: 10 }}>$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={material.costPer ?? ""}
                      onChange={(event) =>
                        updateDraftMaterial({ costPer: event.target.value })
                      }
                      style={{ border: 0, boxShadow: "none" }}
                    />
                  </div>
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Estimated Material Cost
                  <input
                    type="text"
                    readOnly
                    value={estimatedCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                    style={{
                      background: "#e2e8f0",
                      fontWeight: 900,
                    }}
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Purchase Location
                  <input
                    type="text"
                    value={material.purchaseLocation || ""}
                    onChange={(event) =>
                      updateDraftMaterial({
                        purchaseLocation: event.target.value,
                      })
                    }
                    placeholder="Home Depot, Lowe's, supplier, etc."
                  />
                </label>
              </div>

              <label
                style={{
                  display: "grid",
                  gap: 5,
                  marginTop: 12,
                  fontWeight: 800,
                }}
              >
                Material Notes
                <textarea
                  rows="3"
                  value={material.notes || ""}
                  onChange={(event) =>
                    updateDraftMaterial({ notes: event.target.value })
                  }
                  placeholder="Sizes, model numbers, colors, special instructions, etc."
                />
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(material.purchased)}
                  onChange={(event) =>
                    updateDraftMaterial({ purchased: event.target.checked })
                  }
                  style={{ width: 20, height: 20 }}
                />
                Purchased
              </label>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>
                  Photos / Files
                </div>

                {Array.isArray(material.files) && material.files.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                    {material.files.map((file, fileIndex) => (
                      <div key={fileIndex} style={{ position: "relative" }}>
                        <a href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}>
                          {/\.(jpe?g|png|gif|webp)$/i.test(file.name || "") ? (
                            <img
                              src={file.url}
                              alt={file.name}
                              style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: "1px solid #cbd5e1" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 64,
                                height: 64,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid #cbd5e1",
                                borderRadius: 6,
                                background: "#fff",
                                fontSize: 10,
                                textAlign: "center",
                                padding: 4,
                                overflow: "hidden",
                              }}
                            >
                              {file.name}
                            </div>
                          )}
                        </a>
                        <button
                          type="button"
                          onClick={() => removeMaterialFile(index, fileIndex)}
                          title="Remove file"
                          style={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "1px solid #b91c1c",
                            background: "#fee2e2",
                            color: "#b91c1c",
                            fontSize: 13,
                            lineHeight: 1,
                            padding: 0,
                            cursor: "pointer",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={(event) => uploadMaterialFiles(index, event.target.files)}
                />
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="blue-button"
        onClick={addMaterial}
        style={{ marginTop: 14, width: "auto" }}
      >
        + Add Material
      </button>

      <div
        style={{
          marginTop: 14,
          padding: 12,
          borderRadius: 10,
          border: "2px solid #2563eb",
          background: "#eff6ff",
          fontSize: 17,
          fontWeight: 900,
        }}
      >
        Total Estimated Material Cost:{" "}
        {materialsDraft
          .reduce(
            (sum, material) => sum + getMaterialEstimatedCost(material),
            0
          )
          .toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
      </div>

      <div className="modal-buttons">
        <button type="button" className="green-button" onClick={saveMaterials}>
          Save Materials
        </button>
        <button
          type="button"
          className="red-button"
          onClick={() => setShowMaterialsPopup(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  </>,
  document.body
)}

      {showNotePopup && createPortal(
        <>
          <div
            onClick={() => setShowNotePopup(false)}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 1999998,
            }}
          />
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h3>Add Note</h3>
            <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} />
            <input type="file" multiple onChange={(e) => setSelectedFiles([...e.target.files])} />
            <div className="modal-buttons">
              <button className="green-button" onClick={saveNote} disabled={uploading}>
                {uploading ? <LoadingSpinner label="Saving note..." /> : "Save"}
              </button>
              <button className="red-button" onClick={() => setShowNotePopup(false)}>
                Cancel
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* ================= VERIFY ================= */}
      {showVerificationPopup && createPortal(
        <>
          <div
            onClick={() => setShowVerificationPopup(false)}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 1999998,
            }}
          />
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h3>Complete this job?</h3>
            <div className="modal-buttons">
              <button className="green-button" onClick={markJobCompleted}>Yes</button>
              <button className="red-button" onClick={() => setShowVerificationPopup(false)}>No</button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* ================= TIME OFF ================= */}
      {selectedTimeOffRequest && (
        <TimeOffDetailsModal
          entry={selectedTimeOffRequest}
          onClose={() => setSelectedTimeOffRequest(null)}
        />
      )}

      {/* ================= COMPLETE JOB & CREATE INVOICE ================= */}
      {showInvoiceModal && selectedJob && (
        <JobCompletionInvoiceModal
          job={selectedJob}
          onClose={async () => {
            setShowInvoiceModal(false);
            if (!completionSubmitted) return;
            setCompletionSubmitted(false);
            const finishedJob = selectedJob;
            setSelectedJob(null);
            if (jobVisits?.checkOutIfAt) {
              await jobVisits.checkOutIfAt(finishedJob, "job_completed");
            }
            // Reload so Home's job list, SendInvoices and Job History all pick
            // this up. View/date/tab are persisted, so we land right back here.
            refreshKeepingView();
          }}
          onCompleted={() => setCompletionSubmitted(true)}
        />
      )}
    </div>
  );
};

export default EmbeddedCalendar;